import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, Inbox, Mail, Phone, MapPin, Check, Trash2, FileText } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputClass =
  "w-full bg-white border border-zinc-200 rounded-sm px-4 py-3.5 text-sm text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-950 transition-colors duration-300";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("sdc_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const load = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${t}` } });
      setEnquiries(res.data);
    } catch {
      localStorage.removeItem("sdc_admin_token");
      setToken(null);
    }
  }, []);

  useEffect(() => {
    document.title = "Admin | Saurabh Decor Creations";
    if (token) load(token);
  }, [token, load]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("sdc_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("sdc_admin_token");
    setToken(null);
    setEnquiries(null);
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    setEnquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    await axios.delete(`${API}/enquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setEnquiries((prev) => prev.filter((q) => q.id !== id));
  };

  if (!token) {
    return (
      <main data-testid="admin-login" className="min-h-[70vh] flex items-center justify-center pt-[72px] bg-zinc-50">
        <form onSubmit={submitLogin} className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-10">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-500 mb-3">Admin</p>
          <h1 className="font-heading text-3xl font-light tracking-tight text-zinc-950">Enquiry Dashboard</h1>
          <p className="mt-3 text-sm text-zinc-500">Sign in to view every enquiry submitted through the website.</p>
          <div className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Email</label>
              <input data-testid="admin-email-input" id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" className={inputClass} />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Password</label>
              <input data-testid="admin-password-input" id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" className={inputClass} />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-red-700">{error}</p>}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-950 text-white text-sm font-semibold px-8 py-4 rounded-sm hover:bg-zinc-800 disabled:opacity-60 transition-colors duration-300 inline-flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </div>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="pt-[72px] min-h-screen bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-500 mb-3">Admin</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-zinc-950">
              Enquiries{enquiries ? ` (${enquiries.length})` : ""}
            </h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 border border-zinc-200 bg-white rounded-sm px-5 py-2.5 hover:border-zinc-950 hover:text-zinc-950 transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign Out
          </button>
        </div>

        {enquiries === null ? (
          <div className="flex items-center gap-3 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Loading enquiries...
          </div>
        ) : enquiries.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center">
            <Inbox className="w-8 h-8 text-zinc-300 mx-auto" strokeWidth={1.25} />
            <p className="mt-4 text-sm text-zinc-500">No enquiries yet. New submissions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((q) => (
              <article key={q.id} data-testid="admin-enquiry-row" className="bg-white border border-zinc-200 rounded-sm p-6 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-heading text-lg font-medium text-zinc-950">{q.name}</h2>
                    <p className="mt-1 text-xs text-zinc-400">
                      {new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      data-testid={`enquiry-status-${q.id}`}
                      className={`text-xs font-semibold tracking-wide uppercase rounded-sm px-3 py-1.5 ${
                        q.status === "contacted" ? "bg-zinc-950 text-white" : "border border-zinc-200 text-zinc-600"
                      }`}
                    >
                      {q.status === "contacted" ? "Contacted" : "New"}
                    </span>
                    <span className="text-xs font-semibold tracking-wide uppercase text-zinc-600 border border-zinc-200 rounded-sm px-3 py-1.5">
                      {q.project_type}
                    </span>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm text-zinc-600">
                  <a href={`tel:${q.phone}`} className="inline-flex items-center gap-2 hover:text-zinc-950 transition-colors duration-300">
                    <Phone className="w-4 h-4" strokeWidth={1.5} /> {q.phone}
                  </a>
                  <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 hover:text-zinc-950 transition-colors duration-300">
                    <Mail className="w-4 h-4" strokeWidth={1.5} /> {q.email}
                  </a>
                  <span className="inline-flex items-center gap-2">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} /> {q.project_location}
                  </span>
                </div>
                <p className="mt-5 pt-5 border-t border-zinc-100 text-sm leading-relaxed text-zinc-600">{q.message}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to={`/admin/quote/${q.id}`}
                    data-testid={`enquiry-quote-${q.id}`}
                    className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-600 border border-zinc-200 rounded-sm px-4 py-2 hover:border-zinc-950 hover:text-zinc-950 transition-colors duration-300"
                  >
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Create Quote
                  </Link>
                  {q.status === "contacted" ? (
                    <button
                      data-testid={`enquiry-reopen-${q.id}`}
                      onClick={() => setStatus(q.id, "new")}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-600 border border-zinc-200 rounded-sm px-4 py-2 hover:border-zinc-950 hover:text-zinc-950 transition-colors duration-300"
                    >
                      Mark as New
                    </button>
                  ) : (
                    <button
                      data-testid={`enquiry-contacted-${q.id}`}
                      onClick={() => setStatus(q.id, "contacted")}
                      className="inline-flex items-center gap-2 text-xs font-semibold bg-zinc-950 text-white rounded-sm px-4 py-2 hover:bg-zinc-800 transition-colors duration-300"
                    >
                      <Check className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark Contacted
                    </button>
                  )}
                  <button
                    data-testid={`enquiry-delete-${q.id}`}
                    onClick={() => remove(q.id)}
                    className="inline-flex items-center gap-2 text-xs font-semibold text-red-700 border border-zinc-200 rounded-sm px-4 py-2 hover:border-red-700 transition-colors duration-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
